/**
 * <tf-card> — Premium card layout con header/body/footer slots.
 *
 * Uso HTML:
 *   <tf-card title="Statistiche" subtitle="Ultimi 30 giorni">
 *     <p>Contenuto del body...</p>
 *     <div slot="footer">
 *       <tf-button>Apri</tf-button>
 *     </div>
 *   </tf-card>
 *
 *   <tf-card variant="warning" title="Attenzione">
 *     Stai per uscire...
 *   </tf-card>
 *
 * Attributes:
 *   - title: string
 *   - subtitle: string
 *   - variant: 'default' | 'warning' | 'danger' | 'success' | 'info' (border + accent)
 *   - elevation: 'none' | 'sm' | 'md' | 'lg' (shadow)
 *   - padding: 'sm' | 'md' | 'lg' (default 'md')
 *   - clickable: 'true' (presence) — hover effect + cursor-pointer
 *   - href: rende come <a>
 *
 * Slots:
 *   - default: body content
 *   - actions: header right-side (es. menu kebab)
 *   - footer: footer content
 *
 * Events:
 *   - tf-card-click: bubbles se clickable
 *
 * @since v9.16 (Sprint 9)
 */

const VARIANTS = {
  default: 'border-stroke-1 bg-white',
  warning: 'border-amber-200 bg-amber-50/30',
  danger: 'border-red-200 bg-red-50/30',
  success: 'border-emerald-200 bg-emerald-50/30',
  info: 'border-sky-200 bg-sky-50/30',
};

const ELEVATIONS = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
};

const PADDINGS = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export class TfCardElement extends HTMLElement {
  private _capturedDefault = '';
  private _capturedFooter = '';
  private _capturedActions = '';

  static get observedAttributes(): string[] {
    return ['title', 'subtitle', 'variant', 'elevation', 'padding', 'clickable', 'href'];
  }

  connectedCallback(): void {
    this._captureSlots();
    this._render();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this._render();
  }

  private _captureSlots(): void {
    if (this._capturedDefault) return;

    // Slot 'footer'
    const footerSlot = this.querySelector('[slot="footer"]');
    if (footerSlot) {
      this._capturedFooter = footerSlot.outerHTML;
    }

    // Slot 'actions'
    const actionsSlot = this.querySelector('[slot="actions"]');
    if (actionsSlot) {
      this._capturedActions = actionsSlot.outerHTML;
    }

    // Default = tutto il resto (no slot attribute)
    const tempDiv = document.createElement('div');
    Array.from(this.childNodes).forEach((n) => {
      if (n instanceof Element && (n.getAttribute('slot') === 'footer' || n.getAttribute('slot') === 'actions')) {
        return;
      }
      tempDiv.appendChild(n.cloneNode(true));
    });
    this._capturedDefault = tempDiv.innerHTML;
  }

  private _render(): void {
    const title = this.getAttribute('title') || '';
    const subtitle = this.getAttribute('subtitle') || '';
    const variant = (this.getAttribute('variant') as keyof typeof VARIANTS) || 'default';
    const elevation = (this.getAttribute('elevation') as keyof typeof ELEVATIONS) || 'sm';
    const padding = (this.getAttribute('padding') as keyof typeof PADDINGS) || 'md';
    const clickable = this.getAttribute('clickable') === 'true';
    const href = this.getAttribute('href');

    const variantCls = VARIANTS[variant] || VARIANTS.default;
    const elevationCls = ELEVATIONS[elevation] || ELEVATIONS.sm;
    const paddingCls = PADDINGS[padding] || PADDINGS.md;

    const cls = `tf-card-root rounded-2xl border ${variantCls} ${elevationCls}
                 ${clickable || href ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`;

    const headerHtml = (title || subtitle || this._capturedActions)
      ? `<div class="flex items-start justify-between gap-3 ${paddingCls} pb-2">
          <div class="min-w-0 flex-1">
            ${title ? `<h3 class="text-base font-semibold text-slate-900 truncate">${this._esc(title)}</h3>` : ''}
            ${subtitle ? `<p class="text-xs text-slate-500 mt-0.5">${this._esc(subtitle)}</p>` : ''}
          </div>
          ${this._capturedActions || ''}
        </div>`
      : '';

    const hasHeader = !!headerHtml;
    const bodyPadding = hasHeader ? `${paddingCls} pt-2` : paddingCls;

    const bodyHtml = `<div class="tf-card-body ${bodyPadding}">${this._capturedDefault}</div>`;

    const footerHtml = this._capturedFooter
      ? `<div class="tf-card-footer ${paddingCls} pt-3 border-t border-slate-100 mt-2">${this._capturedFooter.replace(/<div slot="footer"/g, '<div').replace(/^<div /, '<div ')}</div>`
      : '';

    if (href && !clickable) {
      // href ma no clickable: render come a wrapper
      this.innerHTML = `<a href="${this._esc(href)}" class="${cls} block no-underline text-inherit">
        ${headerHtml}${bodyHtml}${footerHtml}
      </a>`;
    } else {
      this.innerHTML = `<div class="${cls}" data-tf-card>
        ${headerHtml}${bodyHtml}${footerHtml}
      </div>`;
    }

    if (clickable) {
      this.querySelector<HTMLElement>('[data-tf-card]')?.addEventListener('click', (e) => {
        if (href) {
          window.location.href = href;
          return;
        }
        this.dispatchEvent(new CustomEvent('tf-card-click', { bubbles: true, detail: { event: e } }));
      });
    }
  }

  private _esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
    );
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tf-card')) {
  customElements.define('tf-card', TfCardElement);
}
